import type { SettingsTranslations } from "@/shared/lang/interfaces/settings";
import type { Permission } from "@/shared/types/permission.types";

export interface ApiKeyScopeOption {
  scope: Permission;
  descKey: keyof SettingsTranslations;
}

export interface ApiKeyScopeGroup {
  labelKey: keyof SettingsTranslations;
  scopes: ApiKeyScopeOption[];
}

// Scopes offered when creating an organization API key. Curated to the public
// integration surface (holders, cards, payments, wallets, webhooks) — internal-only scopes
// (operations:*, users:*, api_key:*, organizations:*) are intentionally excluded.
// The backend accepts these flat scopes on POST /api-keys and homologates them to
// the matching RBAC base roles (see rbac.md). `payments:*` powers the public
// payins/payouts/swaps/quotes endpoints and `wallets:*` the balances/statement
// /activation endpoints — both required for an org to run a payin/payout integration.
export const API_KEY_SCOPE_GROUPS: ApiKeyScopeGroup[] = [
  {
    labelKey: "api_keys_scope_group_identities",
    scopes: [
      { scope: "identities:read", descKey: "api_keys_scope_identities_read" },
      { scope: "identities:write", descKey: "api_keys_scope_identities_write" },
    ],
  },
  {
    labelKey: "api_keys_scope_group_payments",
    scopes: [
      { scope: "payments:read", descKey: "api_keys_scope_payments_read" },
      { scope: "payments:write", descKey: "api_keys_scope_payments_write" },
    ],
  },
  {
    labelKey: "api_keys_scope_group_wallets",
    scopes: [
      { scope: "wallets:read", descKey: "api_keys_scope_wallets_read" },
      { scope: "wallets:write", descKey: "api_keys_scope_wallets_write" },
    ],
  },
  {
    labelKey: "api_keys_scope_group_cards",
    scopes: [
      { scope: "cards:read", descKey: "api_keys_scope_cards_read" },
      { scope: "cards:write", descKey: "api_keys_scope_cards_write" },
      { scope: "cards:topup:write", descKey: "api_keys_scope_cards_topup" },
      { scope: "cards:transfer:write", descKey: "api_keys_scope_cards_transfer" },
    ],
  },
  {
    labelKey: "api_keys_scope_group_webhooks",
    scopes: [
      { scope: "organization_webhooks:read", descKey: "api_keys_scope_webhooks_read" },
      { scope: "organization_webhooks:write", descKey: "api_keys_scope_webhooks_write" },
    ],
  },
  // Clients group intentionally omitted for now — only holders (identities),
  // payments, wallets and cards are exposed. The `clients:*` scope i18n keys
  // remain defined for when it's re-enabled.
];
